import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import NotFound from "@/pages/NotFound";

// ---------- Public: the Jewish Music Lab ----------
import LabHome from "@/pages/LabHome";
import LabAbout from "@/pages/LabAbout";
import LabLegal from "@/pages/LabLegal";
import HarpDemo from "@/pages/HarpDemo";
import AlephonicsDemo from "@/pages/AlephonicsDemo";
import SupportLab from "@/pages/SupportLab";
import SupportThanks from "@/pages/SupportThanks";
import ComingSoon from "@/pages/ComingSoon";
import { COMING_SOON } from "@/labConfig";

// ---------- Unlisted: the full studio and the live-event tools ----------
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Library from "@/pages/Library";
import HebrewStudio from "@/pages/HebrewStudio";
import HarpStudio from "@/pages/HarpStudio";
import SharedComposition from "@/pages/SharedComposition";
import LiveAdmin from "@/pages/LiveAdmin";
import LiveAudience from "@/pages/LiveAudience";
import LiveModerator from "@/pages/LiveModerator";
import LiveDisplay from "@/pages/LiveDisplay";
import PreShowCheck from "@/pages/PreShowCheck";

function AppRoutes() {
  const location = useLocation();
  // Catch the OAuth fragment synchronously during render — a useEffect would run
  // after ProtectedRoute has already bounced us to /app/login.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* ================= the lab (public, no sign-in) ================= */}
        {/* Pre-launch, the home page, demos and about page all serve the
            coming-soon holding page. Flip COMING_SOON in labConfig.js to open. */}
        <Route path="/" element={COMING_SOON ? <ComingSoon /> : <LabHome />} />
        <Route path="/about" element={COMING_SOON ? <Navigate to="/" replace /> : <LabAbout />} />
        <Route path="/harp" element={COMING_SOON ? <Navigate to="/" replace /> : <HarpDemo />} />
        <Route path="/alephonics" element={COMING_SOON ? <Navigate to="/" replace /> : <AlephonicsDemo />} />
        <Route path="/alephonics/try-it" element={<Navigate to="/alephonics" replace />} />
        <Route path="/try-it" element={<Navigate to="/alephonics" replace />} />
        <Route path="/support" element={<SupportLab />} />
        <Route path="/support/thanks" element={<SupportThanks />} />
        {/* Old danberrymusic patron links */}
        <Route path="/patrons" element={<Navigate to="/support" replace />} />
        <Route path="/patrons/welcome" element={<Navigate to="/support/thanks" replace />} />
        <Route path="/privacy" element={<LabLegal kind="privacy" />} />
        <Route path="/terms" element={<LabLegal kind="terms" />} />

        {/* ================= unlisted: auth ================= */}
        <Route path="/app/login" element={<Login />} />
        <Route path="/app/auth/callback" element={<AuthCallback />} />

        {/* ================= unlisted: the full studio ================= */}
        <Route path="/app" element={<Navigate to="/app/hebrew" replace />} />
        <Route
          path="/app/library"
          element={
            <ProtectedRoute>
              <Library />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/hebrew"
          element={
            <ProtectedRoute>
              <HebrewStudio />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/harp"
          element={
            <ProtectedRoute>
              <HarpStudio />
            </ProtectedRoute>
          }
        />
        <Route path="/composition/:id" element={<SharedComposition />} />

        {/* ================= unlisted: live events ================= */}
        <Route path="/admin" element={<Navigate to="/admin/live-events" replace />} />
        <Route path="/admin/live-events" element={<LiveAdmin />} />
        <Route path="/live-admin" element={<Navigate to="/admin/live-events" replace />} />
        <Route path="/live/:slug" element={<LiveAudience />} />
        <Route path="/live/:slug/admin" element={<LiveModerator />} />
        <Route path="/live/:slug/display" element={<LiveDisplay />} />
        <Route path="/live/:slug/check" element={<PreShowCheck />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
